/**
 * `/planes` as a `SurfaceController` — VIEW AIRPLANES.
 *
 * Small on purpose: everything that decides anything lives in
 * `model/plane-viewer-model.ts` (pure, tested without a canvas) and
 * `model/plane-viewer-screens.ts` (widgets, same shape as any other panel
 * screen). This class is only the `SurfaceController` glue plus
 * `previewSubject()` — the same contract `career-surface.ts`'s aircraft
 * turntable exposes, reused here for a stock (unpainted) aeroplane rather
 * than a pilot's own.
 */

import { bakedModelIdOf } from "@red-opus/engine";
import type { PreviewPose } from "../career/preview-pose.ts";
import { aceLiveryModelId } from "../planes/ace-livery.ts";
import {
  currentAce,
  currentAircraft,
  initialPlaneViewer,
  planeViewerCommand,
  rosterFor,
  viewerBlurb,
  viewerTitle,
  type PlaneViewerModel,
} from "./model/plane-viewer-model.ts";
import { planeViewerScreen } from "./model/plane-viewer-screens.ts";
import type { MenuAction } from "./model/menu-model.ts";
import type { ShellDialog, ShellScreen } from "./model/shell-screen.ts";
import { ORNAMENT_SHEET } from "./render/board-chrome.ts";
import type { FontId } from "./render/draw-ops.ts";
import { PANEL_SHEET } from "./render/panel-chrome.ts";
import type { SurfaceController } from "./screen-runtime.ts";
import type { FontAtlas } from "./render/text-metrics.ts";

/**
 * **No 2D backdrop.** This screen borrowed PERSONAL AIRCRAFT's `B-AERO.SCR` /
 * `G-AERO.SCR` on the stated grounds that "no dedicated VIEW AIRPLANES capture
 * exists to bake instead". That was wrong on both halves and is now retracted.
 *
 * A capture does exist — `docs/game/ui-reference/view-airplanes-3d.png` — and
 * what it shows is that the original composes nothing behind this screen at
 * all: the aerodrome in it is *3D*, `PTPLANE.TBL` placed by `PTPLANE.WLD`
 * (`planes/aerodrome-stage.ts` has the whole recovery), and the panel chrome
 * sits directly on it. The two `.SCR`s are the career Aerodrome menu's own
 * painted portrait scenes, each with an officer in the foreground and an
 * aeroplane already in it; borrowing one put a photograph of a parked aeroplane
 * behind a rendered one and left the rendered one looking like it was floating
 * in front of a poster, which is the complaint that started roadmap G15.
 *
 * So `background` is `undefined` and the sprite sheets are all this screen
 * loads. The 3D behind the well is `planes/parked-preview.ts`'s.
 */

class PlaneViewerSurface implements SurfaceController {
  readonly assets = {
    images: [],
    sprites: [ORNAMENT_SHEET, PANEL_SHEET],
  };

  private model: PlaneViewerModel = initialPlaneViewer();

  state(): PlaneViewerModel {
    return this.model;
  }

  /**
   * What the 3D inset reads — the current roster entry, in the pose the view
   * arrows set, in the ace's own livery when Famous Aces is browsing a
   * scheme `RB.EXE` really baked for that airframe (`ace-livery.ts`), the
   * factory scheme otherwise.
   *
   * `undefined` while `VIEW WHOSE AIRPLANES?` is still up: the turntable (and
   * the GPU work loading one costs) has no subject to show until a side or
   * Famous Aces has actually been chosen — before that there is only the
   * dialog over the screen.
   */
  previewSubject(): { readonly modelId: string; readonly pose: PreviewPose } | undefined {
    if (this.model.dialog) return undefined;
    const key = currentAircraft(this.model);
    const baseId = key ? bakedModelIdOf(key) : null;
    if (!baseId) return undefined;
    const ace = currentAce(this.model);
    const modelId = ace ? aceLiveryModelId(baseId, ace.id) : baseId;
    return { modelId, pose: this.model.pose };
  }

  screen(fonts: ReadonlyMap<FontId, FontAtlas>): ShellScreen {
    const font = atlas(fonts, "FONT3");
    return planeViewerScreen(
      {
        title: viewerTitle(this.model),
        blurb: viewerBlurb(this.model),
        background: undefined,
        canPage: rosterFor(this.model.mode).length > 1,
      },
      font,
      atlas(fonts, "FONT1"),
    );
  }

  dialog(): ShellDialog | undefined {
    return this.model.dialog;
  }

  focus(): number {
    return this.model.focus;
  }

  setFocus(index: number): void {
    this.model = { ...this.model, focus: index };
  }

  setDialog(dialog: ShellDialog | undefined): void {
    this.model = { ...this.model, dialog };
  }

  typeKey(): boolean {
    return false;
  }

  run(action: MenuAction | undefined): string | undefined {
    if (!action) return undefined;
    if (action.kind === "navigate") return action.path;
    if (action.kind === "close") {
      this.model = { ...this.model, dialog: undefined };
      return undefined;
    }
    if (action.kind !== "command") return undefined;
    const next = planeViewerCommand(this.model, action.command);
    this.model = next.model;
    return next.effect?.kind === "navigate" ? next.effect.path : undefined;
  }
}

function atlas(fonts: ReadonlyMap<FontId, FontAtlas>, id: FontId): FontAtlas {
  const found = fonts.get(id);
  if (!found) throw new Error(`shell: font atlas ${id} was never loaded`);
  return found;
}

export function createPlaneViewerSurface(): PlaneViewerSurface {
  return new PlaneViewerSurface();
}

export type { PlaneViewerSurface };
